import { useMemo, useState } from "react";
import { type PlotData, parsePlotData } from "./plot-data";
import {
  EChartsPlot,
  getSciChartTheme,
  type SciChartTheme,
  useThemeVersion,
} from "./sci-chart";
import { PlotDatasets } from "./sci-plot-datasets";
import { SciTextArea } from "./sci-ui";

export interface PlotShellProps {
  inputLabel: string;
  placeholder: string;
  /** preset sample data loaded on first render */
  example: string;
  exampleLabel: string;
  hint: string;
  // biome-ignore lint/suspicious/noExplicitAny: chart option is deeply typed per plot.
  buildOption: (data: PlotData, theme: SciChartTheme) => any;
  height?: number;
  /** Tool registry id — enables the signed-in "My datasets" save/load bar. */
  toolId?: string;
}

/**
 * Shared plotter widget: a paste-data textarea (auto-parsed) plus a live,
 * theme-reactive ECharts chart built from the data by `buildOption`.
 */
export function PlotShell({
  inputLabel,
  placeholder,
  example,
  exampleLabel,
  hint,
  buildOption,
  height = 400,
  toolId,
}: PlotShellProps) {
  const themeVersion = useThemeVersion();
  const [text, setText] = useState(example);
  const data = useMemo(() => parsePlotData(text), [text]);

  const option = useMemo(() => {
    if (data.matrix.length === 0) return null;
    return buildOption(data, getSciChartTheme());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, themeVersion, buildOption]);

  return (
    <div className="sci-card rounded-2xl p-6 sm:p-8">
      <SciTextArea
        id="plot-input"
        label={inputLabel}
        value={text}
        placeholder={placeholder}
        onChange={setText}
        rows={6}
      />
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setText(example)}
          className="sci-chip rounded-md px-2 py-1 text-xs transition-opacity hover:opacity-80"
        >
          {exampleLabel}
        </button>
        {toolId && (
          <PlotDatasets
            toolId={toolId}
            text={text}
            example={example}
            onLoad={setText}
          />
        )}
      </div>

      {option ? (
        <div className="mt-5">
          <EChartsPlot option={option} height={height} testId="plot-chart" />
        </div>
      ) : (
        <p className="mt-5 text-sm text-muted-foreground">{hint}</p>
      )}
    </div>
  );
}

/** Shared axis styling for cartesian charts. */
export function cartesianAxis(theme: SciChartTheme) {
  return {
    axisLabel: { color: theme.muted, fontSize: 10 },
    axisLine: { lineStyle: { color: theme.border } },
    splitLine: { lineStyle: { color: theme.border, opacity: 0.3 } },
  };
}
